import { useAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { GET_COLLECTED_STYLE_MODEL_LIST } from '~/graphqls/gqls'
import {
  AIOptions,
  AiArtworkOptionTypeEnum,
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_recommendedStyleModelOptions,
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_styleModelOptions,
  getCollectedStyleModelList,
  getCollectedStyleModelList_pictureSocialQuery_collectedStyleModelList,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { modelCodeAtom, resolutionKeyAtom, styleModelAtom } from './atom'

/** 默认选中推荐 */
const DEFAULT_INDEX = 1

export type StyleModelOption =
  | getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_styleModelOptions
  | getCollectedStyleModelList_pictureSocialQuery_collectedStyleModelList
  | getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_recommendedStyleModelOptions

export default function useStyleModel(opts: AIOptions) {
  const [styleModel, setStyleModel] = useAtom(styleModelAtom)
  const [, setModelCode] = useAtom(modelCodeAtom)
  const [, setResolutionKey] = useAtom(resolutionKeyAtom)

  // 原始风格模型
  const [noramlOptions, setNoramlOptions] = useState(() => {
    return opts.styleModelOptions || []
  })
  // 推荐风格模型
  const [recOptions, setRecOptions] = useState(() => {
    return opts.recommendedStyleModelOptions || []
  })

  // 我的收藏风格模型
  const [collectOptions, setCollectOptions] = useState(() => {
    return opts.collectedStyleModelList || []
  })

  const allStyleModelOptions = useMemo(() => {
    return [...recOptions, ...noramlOptions, ...collectOptions]
  }, [recOptions, noramlOptions, collectOptions])

  const [currentIndex, setCurrentIndex] = useState(DEFAULT_INDEX)

  const getCollectedListAction = useImperativeQuery<getCollectedStyleModelList>(
    GET_COLLECTED_STYLE_MODEL_LIST
  )

  const tabs = useMemo(() => {
    let tempSet = new Set<string>()
    noramlOptions?.map(item => {
      tempSet.add(item?.category!)
    })
    return ['收藏', '推荐', ...Array.from(tempSet)]
  }, [noramlOptions])

  const curOptions = useMemo(() => {
    if (currentIndex === 0) {
      return collectOptions
    } else if (currentIndex === 1) {
      return recOptions
    } else {
      return noramlOptions.filter(item => item.category === tabs[currentIndex])
    }
  }, [currentIndex, collectOptions, recOptions, noramlOptions])

  /** 收藏时，更新Options */
  const updateOptions = (
    options: StyleModelOption[],
    key: string,
    collected: boolean
  ) => {
    return options.map(item => {
      if (item.key === key) {
        return {
          ...item,
          collected,
          collectsCount: collected
            ? item.collectsCount + 1
            : item.collectsCount - 1,
        }
      }
      return item
    })
  }

  /** 收藏 */
  const onCollect = (
    key: string,
    collected: boolean,
    type: AiArtworkOptionTypeEnum
  ) => {
    setNoramlOptions(noramlOptions =>
      updateOptions(noramlOptions, key, collected)
    )

    setRecOptions(options => updateOptions(options, key, collected))

    getCollectedListAction().then(res => {
      const collectedOptions =
        res.data.pictureSocialQuery?.collectedStyleModelList || []

      setCollectOptions(collectedOptions)

      // 自定义风格模型被删除了，如果当前选中了该模型，将风格模型重置
      if (
        styleModel === key &&
        type === AiArtworkOptionTypeEnum.CUSTOM_STYLE_PARAM
      ) {
        // 有可能删除后，收藏模型列表为空了
        if (collectedOptions.length) {
          setStyleModel(collectedOptions?.[0]?.key!)
          setModelCode(collectedOptions?.[0]?.modelCode!)
          setResolutionKey(collectedOptions?.[0]?.resolutionKey!)
        } else {
          setStyleModel(recOptions?.[0]?.key!)
          setModelCode(recOptions?.[0]?.modelCode!)
          setResolutionKey(recOptions?.[0]?.resolutionKey!)
        }
      }
    })
  }

  // 如果是再来一张，直接设置的风格模型，如果当前tab下没有该模型，找到匹配的tab
  useEffect(() => {
    if (curOptions.findIndex(item => item?.key === styleModel) > -1) {
      return
    }
    if (recOptions.findIndex(item => item?.key === styleModel) > -1) {
      setCurrentIndex(DEFAULT_INDEX)
    } else if (noramlOptions.findIndex(item => item?.key === styleModel) > -1) {
      const curOption = noramlOptions.find(item => item?.key === styleModel)
      setCurrentIndex(tabs.findIndex(tab => tab === curOption?.category))
    } else {
      setCurrentIndex(DEFAULT_INDEX)
    }
  }, [styleModel])

  return {
    /** 风格模型的tabs */
    styleModelTabs: tabs,
    /** 当前选中的风格模型tab index */
    curStyleModelTabIndex: currentIndex,
    /** 设置当前选中的风格模型tab index */
    setCurStyleModelTabIndex: setCurrentIndex,
    /** 当前应该在页面展示出来的风格模型 */
    curStyleModelOptions: curOptions,
    /** 所有风格模型 */
    allStyleModelOptions,
    /** 收藏 */
    onCollectOption: onCollect,
  }
}
